/** @jsxImportSource theme-ui @jsxRuntime classic */ import {
  Box,
  Container,
  Text,
  Flex,
} from '@tishman/components';
import React, { Fragment, useContext, useMemo } from 'react';

import { Hero, BuyTicketsBar } from '../../components/BuyTickets';
import {
  selectShopCartSelections,
  useAppSelector,
  selectFlow,
  selectShopCartDate,
  selectShopCartAddOnSelections,
  selectWizardCurrentStep,
  selectPerformances,
} from '../../store';
import { formatMoney, formatPriceRange } from '../../utils';
import { isRinkOrRinkMembershipRelated } from '../../services/viva/utils/is-rink-or-rink-membership-related';
import {
  DYNAMIC_PRICING_FLOWS,
  Flow,
  VARIABLE_PRICING_FLOWS,
  checkAdaptivePricing,
} from '../../services/viva/constants';
import { Translatable, useTranslations } from '../../utils/use-translations';
import { BuyTicketsLayoutContext } from '../../layouts/BuyTicketsLayout';

import type { TicketTypesForFlow } from '../../data/translations/ticketType';

const buildDescriptionBlocks = (description: string): React.JSX.Element[] => {
  return description
    .split('\n\n')
    .map((copy: string, i: number) =>
      copy.startsWith('-') ? buildDescriptionList(copy) : <p key={i}>{copy}</p>,
    );
};

const buildDescriptionList = (copyStr: string) => {
  return (
    <Box as="ul" sx={{ listStyle: 'disc', my: 3, pl: 4 }}>
      {copyStr
        .split('-')
        .slice(1)
        .map((element, i) => (
          <li key={i}>{element}</li>
        ))}
    </Box>
  );
};

export const BuyTicketsIntroBlock = ({
  showPrice = true,
  heroTitleWhiteSpace,
  heroTitle,
  heroDescription,
}: {
  showPrice?: boolean;
  heroTitleWhiteSpace?: string;
  heroTitle?: string;
  heroDescription?: string;
}): JSX.Element => {
  const { heroData: _heroData, isLoading } = useContext(
    BuyTicketsLayoutContext,
  );
  const shopCartSelections = useAppSelector(selectShopCartSelections);
  const shopCartAddonSelections = useAppSelector(selectShopCartAddOnSelections);
  const shopCartDate = useAppSelector(selectShopCartDate);
  const shopCartPerformances = useAppSelector(selectPerformances);
  const flow = useAppSelector(selectFlow);
  const currentStep = useAppSelector(selectWizardCurrentStep);

  const hasAdaptivePricing = checkAdaptivePricing(flow);
  const currentShopCartPerformances = shopCartPerformances[flow];

  const translations = useTranslations<TicketTypesForFlow>(
    Translatable.TicketType,
  );

  const price = useMemo(() => {
    // Used when exact prices aren't yet determined
    const priceRangeSum = shopCartSelections.reduce(
      (prev, curr) => ({
        high: prev.high + curr.priceRange.high * curr.count,
        low: prev.low + curr.priceRange.low * curr.count,
      }),
      { high: 0, low: 0 },
    );

    // Used when the exact price has been determined
    const dynamicPriceSum = shopCartSelections.reduce(
      (prev, curr) => ({
        high: prev.high + curr.price * curr.count,
        low: prev.low + curr.price * curr.count,
      }),
      { high: 0, low: 0 },
    );

    const pricingSum =
      hasAdaptivePricing && // Is a flow with dynamic or variable pricing
      shopCartSelections.length && // There are tickets in the <cart></cart>
      currentStep >= 2 && // The is on step 2 or greater
      !isLoading && // The initial performance data has been loaded
      currentShopCartPerformances.length > 0 // There are performances for the performance selected by the user
        ? dynamicPriceSum // Use actual prices when in adaptive pricing mode and past step 2
        : priceRangeSum; // Otherwise use the price range

    // Use shopCartAddonSelections and filter tickets for the current flow
    const addOnsSum = Object.values(shopCartAddonSelections)
      .filter((addon) => addon.flow === flow)
      .reduce((sum, addon) => sum + addon.price * (addon.quantity ?? 0), 0);

    return isRinkOrRinkMembershipRelated(flow) &&
      flow !== Flow.RINK_VIP &&
      flow !== Flow.RINK &&
      flow !== Flow.CHALETS
      ? formatMoney(pricingSum.low + addOnsSum)
      : formatPriceRange(
          (pricingSum.low ?? 0) + addOnsSum,
          (pricingSum.high ?? 0) + addOnsSum,
        );
  }, [
    shopCartSelections,
    shopCartAddonSelections,
    flow,
    shopCartDate,
    currentStep,
    isLoading,
  ]);

  const tax = useMemo(() => {
    const taxSum = shopCartSelections.reduce(
      (prev, curr) => prev + curr.tax * curr.count,
      0,
    );

    return taxSum;
  }, [shopCartSelections, shopCartDate]);

  const heroData = useMemo(() => {
    const { title, link } = _heroData ?? {};
    // TODO: Bring back
    // if (!title) throw new Error('Expected buy tickets page title');

    const descriptionMarkup =
      heroDescription ??
      (_heroData && 'description' in _heroData && _heroData.description
        ? buildDescriptionBlocks(_heroData.description)
        : undefined);

    return {
      link: {
        label: translations?.forFlow[flow]?.link?.label ?? link?.label ?? '',
        modal: link?.modal,
        url: translations?.forFlow[flow]?.link?.url ?? link?.url ?? '',
      },
      title: translations?.forFlow[flow]?.heroTitle ?? title ?? '',
      description: descriptionMarkup ? (
        <Fragment>{descriptionMarkup}</Fragment>
      ) : undefined,
    };
  }, [_heroData]);

  return (
    <Fragment>
      <Container sx={{ mt: 3, pl: [3, 4, 4] }}>
        <Hero
          description={heroData.description}
          fullWidth={!showPrice}
          link={heroData.link}
          title={heroTitle ?? heroData.title}
          titleWhiteSpace={heroTitleWhiteSpace}
        >
          {showPrice && flow !== Flow.CART && (
            <Fragment>
              <Box sx={{ minHeight: '86px' }}>
                <Text sx={{ variant: 'text.ticketsHeading' }}>{price}</Text>

                <Text
                  sx={{
                    fontSize: 2,
                    mt: 0,
                    textAlign: 'right',
                  }}
                >
                  <Box
                    as="span"
                    sx={{
                      fontWeight: 'medium',
                    }}
                  >
                    {hasAdaptivePricing ? '+ Tax' : `+${formatMoney(tax)} Tax`}
                  </Box>
                </Text>
              </Box>
            </Fragment>
          )}
        </Hero>
      </Container>
      <BuyTicketsBar
        price={
          <Box
            mr="2"
            sx={{
              flexGrow: 1,
            }}
          >
            <Flex
              sx={{
                textAlign: 'right',
                alignContent: 'center',
                minHeight: '86px',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'flex-end',
                display: showPrice && flow !== Flow.CART ? 'flex' : 'none',
              }}
            >
              <Text variant="buyTicketsBarTitle">{price}</Text>

              <Text
                sx={{
                  fontSize: 1,
                  mt: 0,
                  textAlign: 'right',
                }}
              >
                <Box
                  as="span"
                  sx={{
                    fontWeight: 'medium',
                  }}
                >
                  {DYNAMIC_PRICING_FLOWS.includes(flow) ||
                  VARIABLE_PRICING_FLOWS.includes(flow)
                    ? '+ Tax'
                    : `+${formatMoney(tax)} Tax`}
                </Box>
              </Text>
            </Flex>
          </Box>
        }
        title={
          <Text
            sx={{ flexGrow: 1, textAlign: ['left', 'right'] }}
            variant="buyTicketsBarTitle"
          >
            {heroData.title}
          </Text>
        }
      />
    </Fragment>
  );
};
